import { useEffect, useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button, Panel } from '../../components/ui'
import { useAuth } from '../../components/auth-context'
import { auth, type AuthOrganization, type AuthSession } from '../../lib/auth'

function displayName(session: AuthSession): string {
  if (!session.user) return 'User'
  const name = `${session.user.firstName ?? ''} ${session.user.lastName ?? ''}`.trim()
  return name || session.user.email
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-6 py-3 first:pt-0 last:pb-0">
      <dt className="shrink-0 text-sm text-text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">{value || '-'}</dd>
    </div>
  )
}

function roleLabel(role: AuthOrganization['role'] | undefined): string {
  return role === 'admin' ? 'Admin' : role === 'member' ? 'Member' : '-'
}

export function ProfilePage() {
  const contextSession = useAuth()
  const [freshSession, setFreshSession] = useState<AuthSession | null>(null)
  const [switchingOrgID, setSwitchingOrgID] = useState<string | null>(null)
  const session = freshSession ?? contextSession
  const currentOrgID = session.organization?.id
  const organizations = session.organizations.length > 0
    ? session.organizations
    : session.organization ? [session.organization] : []

  useEffect(() => {
    let cancelled = false
    auth.clearCache()
    auth.getSession().then((nextSession) => {
      if (!cancelled) setFreshSession(nextSession)
    }).catch(() => {
      if (!cancelled) setFreshSession(contextSession)
    })
    return () => {
      cancelled = true
    }
  }, [contextSession])

  const switchOrganization = (org: AuthOrganization) => {
    if (org.id === currentOrgID || switchingOrgID) return
    if (!org.externalId) {
      toast.error('WorkOS organization required')
      return
    }
    setSwitchingOrgID(org.id)
    auth.switchOrganization(org.externalId)
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-app-bg">
      <PageHeader crumbs={[{ label: 'Profile' }]} />
      <div className="flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <Panel title="User">
            <dl className="divide-y divide-gray-200">
              <DetailRow label="Name" value={displayName(session)} />
              <DetailRow label="Email" value={session.user?.email} />
              <DetailRow label="Current organization" value={session.organization?.name} />
              <DetailRow label="Current role" value={roleLabel(session.organization?.role)} />
            </dl>
          </Panel>

          <Panel title="Organizations">
            {organizations.length === 0 ? (
              <p className="text-sm text-text-muted">No organizations assigned.</p>
            ) : (
              <div className="divide-y divide-gray-200">
                {organizations.map((org) => {
                  const active = org.id === currentOrgID
                  return (
                    <div key={org.id} className="flex items-center justify-between gap-6 py-3 first:pt-0 last:pb-0">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900">{org.name}</div>
                      </div>
                      <div className="flex shrink-0 items-center justify-end gap-3">
                        <span className="text-sm text-text-muted">{roleLabel(org.role)}</span>
                        {active ? (
                          <Button type="button" variant="secondary" disabled>
                            <Check size={14} />
                            Current
                          </Button>
                        ) : (
                          <Button type="button" disabled={switchingOrgID !== null} onClick={() => void switchOrganization(org)}>
                            <ArrowRight size={14} />
                            {switchingOrgID === org.id ? 'Switching...' : 'Switch'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}
